import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Comment from '../types/Comment';
import Post from '../types/Post';
import CreateComment from './CreateComment';
import { LightText, SmallFont } from './Style';
import { useAuth } from '../context/auth';
import styled from 'styled-components';
import { ReactComponent as HeartIcon } from '../img/heart.svg';
import { ReactComponent as HeartFillIcon } from '../img/heart-fill.svg';
import { ReactComponent as ReplyIcon } from '../img/reply.svg';
import api, { API_BASE } from '../api';
import formatText from '../formatText';
import MentionInput from './MentionInput';
import {
  tokensToBracketText,
  mentionsToEditState,
  enrichOrderedMentions,
  resolveMusicMentions,
} from '../mentionUtils';
import { isSpecialAvatarUser, SpecialAvatar } from './UserBadge';

function avatarColor(username: string): string {
  var hash = 0;
  for (var i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  var hue = ((hash % 360) + 360) % 360;
  return 'hsl(' + hue + ', 55%, 55%)';
}

const CommentAvatar = styled.img`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const CommentAvatarCircle = styled.div<{ $bgColor: string }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${function (p: any) {
    return p.$bgColor;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const CommentUsername = styled(Link)`
  font-weight: 900;
  text-decoration: none;
  color: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

const CommentCard = styled.div<{ $standalone?: boolean }>`
  box-sizing: border-box;
  max-width: 400px;
  margin: 0 auto;
  padding: ${(p) => (p.$standalone ? '0.25em 0 0.75em' : '1em 0em')};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75em;
`;

// Single shared treatment for every comment action (reply, edit, delete, save,
// cancel), matching the app-wide action-link language: 0.7rem / 700 /
// tertiary gray, darkening to strong ink on hover.
//
// The label is small but each action is a 24px touch target, the WCAG 2.2
// minimum, chosen over 44px to keep comment threads dense. The height is real
// layout height, so no button overlaps the comment text above or the next
// comment below and nothing steals a tap. The side padding is cancelled by an
// equal negative margin so the labels keep their visual spacing.
const actionTarget = `
  min-height: 24px;
  display: inline-flex;
  align-items: center;
  padding: 0 0.35em;
  margin: 0 -0.35em;
`;

const ReplyButton = styled.button`
  background: none;
  border: none;
  ${actionTarget}
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--gray-tertiary);

  &:hover {
    color: var(--gray-strong);
  }
`;

// All comment actions sit in one row under the text; gap handles spacing so
// individual actions carry no margins.
const CommentMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 0.9em;
  margin-top: 0.15em;
`;

// Edit and Delete reuse the shared action treatment (Delete darkens to tomato),
// so reply / edit / delete read as one consistent set instead of three styles.
const EditLink = styled(ReplyButton)`
  -webkit-tap-highlight-color: transparent;
`;

const DeleteLink = styled(ReplyButton)`
  -webkit-tap-highlight-color: transparent;

  &:hover,
  &:active {
    color: tomato;
  }
`;

const EditTextarea = styled.textarea`
  width: 100%;
  min-height: 40px;
  font-size: 0.9em;
  line-height: 1.4;
  font-family: inherit;
  border: 1px solid var(--gray-divider);
  border-radius: var(--radius-card);
  padding: 0.4em;
  resize: vertical;
  box-sizing: border-box;
`;

const RepliesContainer = styled.div`
  margin-left: 1.5em;
  padding-left: 0.75em;
  border-left: 1px solid var(--gray-divider);
`;

const CommentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5em;
  margin-bottom: 0.25em;
`;

const CommentText = styled.div`
  flex: 1;
  min-width: 0;
  white-space: pre-wrap;
  font-size: 0.9em;
`;

const CommentTextInner = styled.span<{ collapsed?: boolean }>`
  ${function (p: any) {
    return p.collapsed
      ? `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `
      : '';
  }}
`;

const CommentReadMore = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--gray-tertiary);
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: var(--gray-divider);
  text-underline-offset: 2px;
  margin-top: 0.15em;
  display: block;

  &:hover {
    color: var(--gray-strong);
    text-decoration-color: var(--gray-strong);
  }
`;

// Like now lives inline in the action row (see CommentMeta), so it uses the
// same action-link treatment as reply/edit/delete, with the heart inline.
const LikeButton = styled.button`
  background: none;
  border: none;
  ${actionTarget}
  cursor: pointer;
  gap: 0.25em;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--gray-tertiary);

  &:hover {
    color: var(--gray-strong);
  }
`;

const LikeCount = styled.span`
  font-size: 0.65rem;
  color: inherit;
`;

function ClampedCommentText(props: {
  text: string;
  postId: string;
  fullText?: boolean;
  musicMentions?: any[];
  postArtist?: string;
}) {
  var ref = useRef<HTMLSpanElement>(null);
  var _overflows = useState(false);
  var overflows = _overflows[0];
  var setOverflows = _overflows[1];
  var _expanded = useState(false);
  var expanded = _expanded[0];
  var setExpanded = _expanded[1];

  useEffect(
    function () {
      var el = ref.current;
      if (el && !props.fullText) {
        setOverflows(el.scrollHeight > el.clientHeight + 1);
      }
    },
    [props.text, props.fullText]
  );

  if (props.fullText) {
    return (
      <span
        dangerouslySetInnerHTML={{
          __html: formatText(props.text, props.musicMentions, props.postArtist),
        }}
      />
    );
  }

  return (
    <>
      <CommentTextInner
        ref={ref}
        collapsed={!expanded && !props.fullText}
        onClick={
          overflows && !expanded
            ? function () {
                setExpanded(true);
              }
            : undefined
        }
        style={overflows && !expanded ? { cursor: 'pointer' } : undefined}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: formatText(props.text, props.musicMentions, props.postArtist),
          }}
        />
      </CommentTextInner>
      {overflows && !expanded && (
        <CommentReadMore
          onClick={function () {
            setExpanded(true);
          }}
        >
          Read more
        </CommentReadMore>
      )}
    </>
  );
}

interface CommentsProps {
  post: Post;
  comments: Comment[];
  onCommentAdded: (comment: Comment) => void;
  likedCommentsMap?: Record<string, boolean>;
  onCommentLikeToggle?: (commentId: string, liked: boolean) => void;
  onCommentDeleted?: (commentId: string, parentCommentId?: string) => void;
  onCommentEdited?: (commentId: string, newText: string, newMusicMentions?: any[]) => void;
  fullText?: boolean;
  // Rendered in a card of its own (the list page) rather than under a post:
  // the card supplies the top spacing, and there is nothing to divide from.
  standalone?: boolean;
  autoFocus?: boolean;
  initialReplyToId?: string | null;
  initialReplyToUsername?: string | null;
  onClose?: () => void;
}

export default function Comments(props: CommentsProps) {
  // Safe to use props as initial state: the sheet unmounts on close and remounts fresh on each open
  var _replyingTo = useState<string | null>(props.initialReplyToId || null);
  var replyingTo = _replyingTo[0];
  var setReplyingTo = _replyingTo[1];

  var _replyToUsername = useState<string | null>(props.initialReplyToUsername || null);
  var replyToUsername = _replyToUsername[0];
  var setReplyToUsername = _replyToUsername[1];

  var _editingId = useState<string | null>(null);
  var editingId = _editingId[0];
  var setEditingId = _editingId[1];

  var _editText = useState('');
  var editText = _editText[0];
  var setEditText = _editText[1];

  var _editMusicMentions = useState<any[]>([]);
  var editMusicMentions = _editMusicMentions[0];
  var setEditMusicMentions = _editMusicMentions[1];
  var editGetSubmitTextRef = useRef<(() => { text: string; mentions: any[] }) | null>(null);

  var _editSaving = useState(false);
  var editSaving = _editSaving[0];
  var setEditSaving = _editSaving[1];

  // Capture the initial liked state on first render so we can compute optimistic adjustments
  var initialLikedRef = useRef<Record<string, boolean> | null>(null);
  if (initialLikedRef.current === null && props.likedCommentsMap) {
    initialLikedRef.current = Object.assign({}, props.likedCommentsMap);
  }

  var authContext = useAuth();
  var isLoggedIn = authContext.authTokens && authContext.authTokens.token;
  var currentUserId =
    authContext.authTokens && authContext.authTokens.user && authContext.authTokens.user.id;

  function handleLike(commentId: string) {
    if (!props.onCommentLikeToggle) return;
    var isLiked = props.likedCommentsMap && props.likedCommentsMap[commentId];
    props.onCommentLikeToggle(commentId, !isLiked);
    api.post('/api/comment-likes/' + commentId).catch(function () {
      // Revert on failure
      if (props.onCommentLikeToggle) {
        props.onCommentLikeToggle(commentId, !!isLiked);
      }
    });
  }

  function handleDelete(commentId: string, parentCommentId?: string) {
    if (!window.confirm('Delete this comment?')) return;
    api
      .delete('/api/comments/' + commentId)
      .then(function () {
        if (props.onCommentDeleted) {
          props.onCommentDeleted(commentId, parentCommentId);
        }
      })
      .catch(function (err) {
        console.error('Delete comment failed:', err);
      });
  }

  function handleEditStart(comment: Comment) {
    setEditingId(comment.id);
    var mentions = comment.musicMentions || [];
    setEditText(mentions.length > 0 ? tokensToBracketText(comment.text, mentions) : comment.text);
    setEditMusicMentions(mentions.length > 0 ? mentionsToEditState(mentions) : []);
  }

  function handleEditSave(commentId: string) {
    if (!editText.trim()) return;
    var submitData = editGetSubmitTextRef.current ? editGetSubmitTextRef.current() : null;
    var finalText = ((submitData ? submitData.text : null) || editText).trim();
    var orderedMentions = submitData
      ? enrichOrderedMentions(submitData.mentions, editMusicMentions)
      : editMusicMentions;
    setEditSaving(true);

    resolveMusicMentions(orderedMentions).then(function (resolved) {
      api
        .put('/api/comments/' + commentId, {
          text: finalText,
          musicMentions: resolved,
        })
        .then(function (res: any) {
          var serverText = (res && res.data && res.data.text) || finalText;
          if (props.onCommentEdited) {
            props.onCommentEdited(
              commentId,
              serverText,
              resolved.map(function (md: any) {
                return Object.assign({}, md, { id: '' });
              })
            );
          }
          setEditingId(null);
          setEditText('');
        })
        .catch(function (err) {
          console.error('Edit comment failed:', err);
        })
        .finally(function () {
          setEditSaving(false);
        });
    });
  }

  function handleEditCancel() {
    setEditingId(null);
    setEditText('');
    setEditMusicMentions([]);
  }

  function renderEditLink(comment: Comment) {
    if (!isLoggedIn || !currentUserId || comment.user.id !== currentUserId) return null;
    return (
      <EditLink
        onClick={function () {
          handleEditStart(comment);
        }}
      >
        Edit
      </EditLink>
    );
  }

  function renderLikeButton(comment: Comment) {
    if (!isLoggedIn) return null;
    var isLiked = props.likedCommentsMap && props.likedCommentsMap[comment.id];
    var wasInitiallyLiked = initialLikedRef.current && initialLikedRef.current[comment.id];
    var serverCount = comment.likeCount || 0;
    var adjustedCount = serverCount + (isLiked ? 1 : 0) - (wasInitiallyLiked ? 1 : 0);
    if (adjustedCount < 0) adjustedCount = 0;
    return (
      <LikeButton
        onClick={function () {
          handleLike(comment.id);
        }}
      >
        {isLiked ? <HeartFillIcon width="12" height="12" /> : <HeartIcon width="12" height="12" />}
        {adjustedCount > 0 && <LikeCount>{adjustedCount}</LikeCount>}
      </LikeButton>
    );
  }

  function renderDeleteLink(comment: Comment, parentId?: string) {
    if (!isLoggedIn || !currentUserId || comment.user.id !== currentUserId) return null;
    return (
      <DeleteLink
        onClick={function () {
          handleDelete(comment.id, parentId);
        }}
      >
        Delete
      </DeleteLink>
    );
  }

  if (props.comments.length < 1) {
    return (
      <>
        <CommentCard $standalone={props.standalone}>
          <CreateComment
            post={props.post}
            onCommentAdded={props.onCommentAdded}
            autoFocus={props.autoFocus}
            onCancel={props.onClose}
            noDivider={props.standalone}
          />
        </CommentCard>
      </>
    );
  }

  return (
    <CommentCard $standalone={props.standalone}>
      <SmallFont>
        <LightText>
          {props.comments.map(function (comment) {
            return (
              <div key={comment.id} id={'comment-' + comment.id}>
                <CommentRow>
                  {isSpecialAvatarUser(comment.user.username) ? (
                    <SpecialAvatar size={22} />
                  ) : comment.user.profilePictureURL ? (
                    <CommentAvatar
                      src={API_BASE + comment.user.profilePictureURL}
                      alt={comment.user.username}
                    />
                  ) : (
                    <CommentAvatarCircle $bgColor={avatarColor(comment.user.username)}>
                      {comment.user.username.charAt(0).toUpperCase()}
                    </CommentAvatarCircle>
                  )}
                  <CommentText>
                    <CommentUsername to={'/profile/' + comment.user.username}>
                      {comment.user.username}
                    </CommentUsername>{' '}
                    {editingId === comment.id ? (
                      <>
                        <MentionInput
                          value={editText}
                          onChange={function (e: any) {
                            setEditText(e.target.value);
                          }}
                          onKeyDown={function (e: any) {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleEditSave(editingId!);
                            }
                          }}
                          musicMentions={editMusicMentions}
                          onMusicMentionsChange={setEditMusicMentions}
                          getSubmitTextRef={editGetSubmitTextRef}
                          minRows={2}
                          disabled={editSaving}
                          autoFocus
                          style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.85rem' }}
                        />
                        <CommentMeta>
                          <ReplyButton
                            onClick={function () {
                              handleEditSave(comment.id);
                            }}
                            style={editSaving ? { opacity: 0.5 } : undefined}
                          >
                            {editSaving ? 'Saving...' : 'Save'}
                          </ReplyButton>
                          <EditLink onClick={handleEditCancel}>Cancel</EditLink>
                        </CommentMeta>
                      </>
                    ) : (
                      <>
                        <ClampedCommentText
                          text={comment.text}
                          postId={props.post.id}
                          fullText={props.fullText}
                          musicMentions={comment.musicMentions}
                          postArtist={props.post.media?.artist}
                        />
                        <CommentMeta>
                          {renderLikeButton(comment)}
                          {isLoggedIn && (
                            <ReplyButton
                              onClick={function () {
                                setReplyingTo(comment.id);
                                setReplyToUsername(comment.user.username);
                              }}
                            >
                              Reply
                            </ReplyButton>
                          )}
                          {renderEditLink(comment)}
                          {renderDeleteLink(comment)}
                        </CommentMeta>
                      </>
                    )}
                  </CommentText>
                </CommentRow>
                {comment.replies && comment.replies.length > 0 && (
                  <RepliesContainer>
                    {comment.replies.map(function (reply) {
                      return (
                        <CommentRow key={reply.id} id={'comment-' + reply.id}>
                          {isSpecialAvatarUser(reply.user.username) ? (
                            <SpecialAvatar size={22} />
                          ) : reply.user.profilePictureURL ? (
                            <CommentAvatar
                              src={API_BASE + reply.user.profilePictureURL}
                              alt={reply.user.username}
                            />
                          ) : (
                            <CommentAvatarCircle $bgColor={avatarColor(reply.user.username)}>
                              {reply.user.username.charAt(0).toUpperCase()}
                            </CommentAvatarCircle>
                          )}
                          <CommentText>
                            <CommentUsername to={'/profile/' + reply.user.username}>
                              {reply.user.username}
                            </CommentUsername>{' '}
                            {editingId === reply.id ? (
                              <>
                                <EditTextarea
                                  value={editText}
                                  onChange={function (e: any) {
                                    setEditText(e.target.value);
                                  }}
                                  onKeyDown={function (e: any) {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      handleEditSave(reply.id);
                                    }
                                  }}
                                  disabled={editSaving}
                                  autoFocus
                                />
                                <CommentMeta>
                                  <ReplyButton
                                    onClick={function () {
                                      handleEditSave(reply.id);
                                    }}
                                    style={editSaving ? { opacity: 0.5 } : undefined}
                                  >
                                    {editSaving ? 'Saving...' : 'Save'}
                                  </ReplyButton>
                                  <EditLink onClick={handleEditCancel}>Cancel</EditLink>
                                </CommentMeta>
                              </>
                            ) : (
                              <>
                                <ClampedCommentText
                                  text={reply.text}
                                  postId={props.post.id}
                                  fullText={props.fullText}
                                  musicMentions={reply.musicMentions}
                                  postArtist={props.post.media?.artist}
                                />
                                <CommentMeta>
                                  {renderLikeButton(reply)}
                                  {isLoggedIn && (
                                    <ReplyButton
                                      onClick={function () {
                                        setReplyingTo(comment.id);
                                        setReplyToUsername(reply.user.username);
                                      }}
                                    >
                                      Reply
                                    </ReplyButton>
                                  )}
                                  {renderEditLink(reply)}
                                  {renderDeleteLink(reply, comment.id)}
                                </CommentMeta>
                              </>
                            )}
                          </CommentText>
                        </CommentRow>
                      );
                    })}
                  </RepliesContainer>
                )}
                {replyingTo === comment.id && (
                  <CreateComment
                    post={props.post}
                    parentCommentId={comment.id}
                    initialText={replyToUsername ? '{@' + replyToUsername + '} ' : ''}
                    autoFocus={props.autoFocus}
                    onCommentAdded={function (newComment) {
                      setReplyingTo(null);
                      setReplyToUsername(null);
                      props.onCommentAdded(newComment);
                    }}
                    onCancel={function () {
                      setReplyingTo(null);
                      setReplyToUsername(null);
                    }}
                  />
                )}
              </div>
            );
          })}
        </LightText>
      </SmallFont>
      {!replyingTo && (
        <CreateComment
          post={props.post}
          onCommentAdded={props.onCommentAdded}
          autoFocus={props.autoFocus}
          onCancel={props.onClose}
        />
      )}
    </CommentCard>
  );
}
